import { portalApiClient } from '@/platform/http'
import type {
  AddKitEventDTOType,
  ClientRegisterKitDTOType,
  DashboardAggregationPeriodsType,
  GetOrdersByStateSchemaType,
  InsuranceItemType,
  KitRejectionsSummarySchemaType,
  OrderAdditionalFieldSchemaType,
  OrderPaginatedSchemaType,
  OrdersByStateItemSchemaType,
  PlaceOrderDTOType,
  ProviderSchemaType,
  QuestionnaireDTOType,
  RecentOrdersSearchRequestDTOType,
  RequestMultipleTestOrderCorrectionsDTOType,
  RequestMultipleTestOrderCorrectionsSchemaType,
  SetTestOrderStatusDTOType,
} from '@/types/portal-api.generated'
import type { AxiosRequestConfig } from 'axios'
import { isErrorFromPath } from '@zodios/core'
import { isHttpNotFoundError } from '@/utilities/axios/errorUtils'
import { OrdersTimeMetric } from '@/utilities/orders/timeMetrics'
import { batch } from '@/utilities/async/batch'
import { PORTAL_TIME_ZONE } from '@/utilities/timeZone'

const ORDER_ID_KEY = 'ash-ordered-ids'

const DEFAULT_TIME_METRICS: OrdersTimeMetric[] = ['turnaround_time', 'lab_processing_time']
const inFlightDashboardRequests = new Map<string, Promise<unknown>>()

export type OrdersByStateItem = OrdersByStateItemSchemaType
export type OrdersByStateResponse = GetOrdersByStateSchemaType

const getDashboardRequestKey = (endpoint: string, queries: Record<string, unknown>): string =>
  `${endpoint}:${JSON.stringify(queries)}`

const dedupeDashboardRequest = <T>(
  endpoint: string,
  queries: Record<string, unknown>,
  requestFactory: () => Promise<T>,
): Promise<T> => {
  const requestKey = getDashboardRequestKey(endpoint, queries)
  const existingRequest = inFlightDashboardRequests.get(requestKey) as Promise<T> | undefined
  if (existingRequest) {
    return existingRequest
  }

  const request = requestFactory()
  const trackedRequest = request.finally(() => {
    if (inFlightDashboardRequests.get(requestKey) === trackedRequest) {
      inFlightDashboardRequests.delete(requestKey)
    }
  })

  inFlightDashboardRequests.set(requestKey, trackedRequest as Promise<unknown>)
  return trackedRequest
}

export const getOrdersTimeMetrics = async (
  partnerId: string,
  startDate: string,
  endDate: string,
  metrics: OrdersTimeMetric[] = DEFAULT_TIME_METRICS,
) => {
  const queries = {
    partner_id: partnerId,
    start_date: startDate,
    end_date: endDate,
    metrics,
    time_zone: PORTAL_TIME_ZONE,
  }

  return dedupeDashboardRequest('orders-time-metrics', queries, async () => {
    const data = await portalApiClient.get_time_metrics_v3_clients_orders_time_metrics_get({
      queries,
    })
    return data
  })
}

export const getOrdersAggregatedData = async (
  partnerId: string,
  startDate: string,
  endDate: string,
  aggregationPeriod: DashboardAggregationPeriodsType,
  status_keys?: string[],
) => {
  const queries = {
    partner_id: partnerId,
    start_date: startDate,
    end_date: endDate,
    aggregation_period: aggregationPeriod,
    time_zone: PORTAL_TIME_ZONE,
    ...(status_keys !== undefined && status_keys.length > 0 ? { status_keys } : {}),
  }

  return dedupeDashboardRequest('orders-aggregated-data', queries, async () => {
    try {
      const data = await portalApiClient.get_aggregated_data_v3_clients_orders_aggregated_data_get({
        queries,
      })
      return { error: null, data }
    } catch (error) {
      const normalizedError =
        error instanceof Error
          ? error
          : new Error('Orders aggregated request failed', { cause: error })
      return { error: normalizedError, data: null }
    }
  })
}

export const getOrdersEventCount = async (
  partnerId: string,
  endDate: string,
  periodInDays: number,
) => {
  const queries = {
    partner_id: partnerId,
    end_date: endDate,
    period_in_days: periodInDays,
    time_zone: PORTAL_TIME_ZONE,
  }

  return dedupeDashboardRequest('orders-event-count', queries, async () => {
    try {
      const data = await portalApiClient.results_count_v2_clients_orders_event_count_get({
        queries,
      })
      return { error: null, data }
    } catch (error) {
      const normalizedError =
        error instanceof Error
          ? error
          : new Error('Orders event count request failed', { cause: error })
      return { error: normalizedError, data: null }
    }
  })
}

export const getOrdersByState = async (
  partnerId: string,
  options?: {
    startDate?: string | null
    endDate?: string | null
  },
): Promise<OrdersByStateResponse> => {
  const queries: {
    partner_id: string
    time_zone: string
    start_date?: string
    end_date?: string
  } = {
    partner_id: partnerId,
    time_zone: PORTAL_TIME_ZONE,
  }
  if (options?.startDate && options?.endDate) {
    queries.start_date = options.startDate
    queries.end_date = options.endDate
  }

  return dedupeDashboardRequest('orders-by-state', queries, () =>
    portalApiClient.get_orders_by_state_v3_clients_orders_by_state_get({
      queries,
    }),
  )
}

export type GetRecentOrdersResponse = {
  error: Error | null
  data: OrderPaginatedSchemaType | null
  config?: AxiosRequestConfig
}

/** Sort/filter body for recent orders (paging + partner scoped at call site). */
export type GetRecentOrdersSortParams = Omit<
  RecentOrdersSearchRequestDTOType,
  'partner_id' | 'partner_ids' | 'page' | 'page_size'
>

export const getRecentOrders = async (
  partnerId: string,
  page: number,
  pageSize: number,
  sortParams: GetRecentOrdersSortParams = {},
  axiosConfig: Pick<AxiosRequestConfig, 'signal'> = {},
): Promise<GetRecentOrdersResponse> => {
  try {
    const body: RecentOrdersSearchRequestDTOType = {
      partner_id: partnerId,
      page,
      page_size: pageSize,
      ...sortParams,
    }

    const data =
      await portalApiClient.search_most_recent_orders_for_partner_v3_clients_orders_recent_post(
        body,
        axiosConfig,
      )
    return { error: null, data, config: { ...axiosConfig, data: body } }
  } catch (error) {
    const normalizedError =
      error instanceof Error ? error : new Error('Failed to fetch recent orders', { cause: error })
    return { error: normalizedError, data: null }
  }
}

export const cancelOrder = async (
  partnerId: string,
  orderId: string,
): Promise<{ error: Error | null; data: unknown }> => {
  try {
    const data = await portalApiClient.cancel_fulfillment_v2_clients_orders_cancel_post({
      partner_id: partnerId,
      order_id: orderId,
    })
    return { error: null, data }
  } catch (error) {
    const normalizedError =
      error instanceof Error ? error : new Error('Failed to cancel order', { cause: error })
    return { error: normalizedError, data: null }
  }
}

export async function getSKUs(clientId: string, isTestOrder?: boolean) {
  const pageSize = 100
  const startPage = 1

  const queries = {
    partner_id: clientId,
    page_size: pageSize,
    page: startPage,
    ...(isTestOrder ? { is_test_order: true } : {}),
  }

  // Fetch the first page
  const firstPageResponse = await portalApiClient.get_skus_v2_clients_orders_skus_get({
    queries,
  })

  const totalItems = firstPageResponse.total_items || 0
  const totalDesired = totalItems

  // If we got all items in the first page, return immediately
  if (totalDesired <= firstPageResponse.items.length) {
    return { data: firstPageResponse }
  }

  // Calculate how many additional pages we need to fetch
  const remainingItems = totalDesired - firstPageResponse.items.length
  const remainingPages = Math.ceil(remainingItems / pageSize)
  const pagesToFetch = Array.from({ length: remainingPages }, (_, i) => startPage + i + 1)

  // Fetch remaining pages in parallel with a max concurrency of 4
  const otherPages = await batch(
    pagesToFetch,
    1, // batch size of 1 since each page is a separate request
    async ([page]) => {
      const response = await portalApiClient.get_skus_v2_clients_orders_skus_get({
        queries: { ...queries, page },
      })
      return response.items
    },
    4, // max concurrency
  )

  // Combine all results
  const allSkus = firstPageResponse.items.concat(otherPages)

  // Pretend that we got all the pages in one request.
  return {
    data: {
      items: allSkus,
      page_size: pageSize * Math.ceil(totalItems / pageSize),
      page: startPage,
      total_pages: 1,
      total_items: totalItems,
    },
  }
}

export type GetPartnerAdditionalFieldsResponse = {
  error: Error | null
  data: OrderAdditionalFieldSchemaType[] | null
}

export const getAoeQuestionnaire = async (
  clientId: string,
  orderSku: string,
  axiosConfig: Pick<AxiosRequestConfig, 'signal'> = {},
): Promise<QuestionnaireDTOType> =>
  portalApiClient.get_aoe_questionnaire_v3_clients_orders_aoe_questionnaire_get({
    queries: {
      partner_id: clientId,
      order_sku: orderSku,
    },
    ...axiosConfig,
  })

export const getInsurancePlans = async (
  clientId: string,
  labId: number,
  axiosConfig: Pick<AxiosRequestConfig, 'signal'> = {},
): Promise<InsuranceItemType[]> => {
  const response = await portalApiClient.get_insurance_plans_v2_clients_orders_insurance_plans_get({
    queries: {
      partner_id: clientId,
      lab_id: labId,
    },
    ...axiosConfig,
  })
  return response.items ?? []
}

export const getPartnerAdditionalFields = async (
  clientId: string,
): Promise<GetPartnerAdditionalFieldsResponse> => {
  try {
    const data =
      await portalApiClient.get_order_additional_fields_v2_clients_orders_additional_fields_get({
        queries: {
          partner_id: clientId,
        },
      })
    return { error: null, data }
  } catch (error) {
    const normalizedError =
      error instanceof Error
        ? error
        : new Error('Failed to fetch additional fields', { cause: error })
    return { error: normalizedError, data: null }
  }
}

export type GetOrderProvidersResponse = {
  error: Error | null
  data: ProviderSchemaType[] | null
}

export type GetKitRejectionsOptions = {
  dateFrom?: string
  dateTo?: string
}

export type GetKitRejectionsResponse = {
  error: Error | null
  data: KitRejectionsSummarySchemaType | null
}

export const getKitRejections = async (
  partnerId: string,
  options: GetKitRejectionsOptions = {},
): Promise<GetKitRejectionsResponse> => {
  try {
    const data = await portalApiClient.get_kit_rejections_v2_clients_orders_rejections_get({
      queries: {
        partner_id: partnerId,
        ...(options.dateFrom ? { date_from: options.dateFrom } : {}),
        ...(options.dateTo ? { date_to: options.dateTo } : {}),
      },
    })
    return { error: null, data }
  } catch (error) {
    const normalizedError =
      error instanceof Error ? error : new Error('Failed to fetch kit rejections', { cause: error })
    return { error: normalizedError, data: null }
  }
}

export const getOrderProviders = async (clientId: string): Promise<GetOrderProvidersResponse> => {
  try {
    const data = await portalApiClient.get_order_providers_v2_clients_orders_providers_get({
      queries: {
        partner_id: clientId,
      },
    })
    return { error: null, data }
  } catch (error) {
    const normalizedError =
      error instanceof Error ? error : new Error('Failed to fetch providers', { cause: error })
    return { error: normalizedError, data: null }
  }
}

export async function placeOrder(clientId: string, payload: PlaceOrderDTOType) {
  try {
    const data = await portalApiClient.place_order_v2_clients_orders__partner_id__post(payload, {
      params: { partner_id: clientId },
    })

    // Store BE-generated order id for pending row tracking.
    const serverOrderId = data?.order_id || data?.orderId
    if (serverOrderId) {
      const existingOrders = JSON.parse(localStorage.getItem(ORDER_ID_KEY) || '[]')
      const updatedOrders = existingOrders.concat({
        id: serverOrderId,
        partnerId: clientId,
        timestamp: Date.now(),
      })
      localStorage.setItem(ORDER_ID_KEY, JSON.stringify(updatedOrders))
    }

    return { error: null, data }
  } catch (e) {
    let errorMessage = e instanceof Error ? e.message : 'Error placing order: ' + e

    if (isErrorFromPath(portalApiClient.api, 'post', '/v2/clients/orders/:partner_id', e)) {
      const detail = e.response?.data?.detail as string | Array<{ msg?: string }> | undefined
      if (typeof detail === 'string') {
        errorMessage = detail
      } else if (Array.isArray(detail) && detail[0]) {
        const firstError = detail[0] as { msg?: string }
        errorMessage = firstError.msg ?? errorMessage
      } else if (e instanceof Error && e.message) {
        errorMessage = e.message
      }
    }

    return {
      error: errorMessage,
      data: null,
    }
  }
}

// Set completion status for a list of kit IDs
export const setOrderCompletionStatus = async (
  kitIds: string[],
  completed: boolean,
  partnerId: string,
): Promise<{ error: Error | null; data: unknown }> => {
  try {
    const data = await portalApiClient.set_completion_v3_clients_orders_completion_post(
      {
        kit_ids: kitIds,
        completed,
      },
      {
        queries: {
          partner_id: partnerId,
        },
      },
    )
    return { error: null, data }
  } catch (error) {
    const normalizedError =
      error instanceof Error
        ? error
        : new Error('Failed to set completion status', { cause: error })
    return { error: normalizedError, data: null }
  }
}

// Track pending orders in localStorage while awaiting server appearance
export type StoredOrderTrack = { id: string; partnerId: string; timestamp: number }

function isStoredOrderTrack(value: unknown): value is StoredOrderTrack {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).id === 'string' &&
    typeof (value as Record<string, unknown>).partnerId === 'string' &&
    typeof (value as Record<string, unknown>).timestamp === 'number'
  )
}

function getOrderIdsFromRecentResponse(data: OrderPaginatedSchemaType | null): Set<string> {
  if (!data?.orders?.length) return new Set()
  const ids = new Set<string>()
  for (const order of data.orders) {
    const id = order.orderId ?? (order as { order_id?: string }).order_id
    if (typeof id === 'string') ids.add(id)
  }
  return ids
}

/**
 * Returns { count, overdueIds } where overdueIds are pending orders older than 5 minutes.
 */
export const getPendingOrderDetail = async (
  partnerId: string,
): Promise<{ count: number; overdueIds: string[] }> => {
  try {
    if (typeof window === 'undefined') return { count: 0, overdueIds: [] }
    const rawParsed = JSON.parse(localStorage.getItem(ORDER_ID_KEY) || '[]') as unknown
    const rawList = Array.isArray(rawParsed) ? rawParsed : []

    const allStored: StoredOrderTrack[] = rawList.filter(isStoredOrderTrack)
    if (allStored.length !== rawList.length) {
      localStorage.setItem(ORDER_ID_KEY, JSON.stringify(allStored))
    }

    const candidate: StoredOrderTrack[] = allStored.filter((o) => o.partnerId === partnerId)
    if (candidate.length === 0) return { count: 0, overdueIds: [] }

    const THREE_HOURS_MS = 3 * 60 * 60 * 1000
    const FIVE_MINUTES_MS = 5 * 60 * 1000
    const now = Date.now()

    let existingOrderIds = new Set<string>()
    try {
      const response = await getRecentOrders(partnerId, 1, 100)
      if (!response.error && response.data) {
        existingOrderIds = getOrderIdsFromRecentResponse(response.data)
      }
    } catch {
      // ignore fetch errors
    }

    const pendingForPartner: StoredOrderTrack[] = []
    const updatedAll: StoredOrderTrack[] = allStored.filter((stored) => {
      if (stored.partnerId !== partnerId) return true
      const isFresh = now - stored.timestamp <= THREE_HOURS_MS
      if (!isFresh) return false
      const isKnownServerOrder = existingOrderIds.has(stored.id)
      const isPending = !isKnownServerOrder
      if (isPending) pendingForPartner.push(stored)
      return isPending
    })

    localStorage.setItem(ORDER_ID_KEY, JSON.stringify(updatedAll))

    const overdueIds = pendingForPartner
      .filter((o) => now - o.timestamp >= FIVE_MINUTES_MS)
      .map((o) => o.id)

    return { count: pendingForPartner.length, overdueIds }
  } catch {
    return { count: 0, overdueIds: [] }
  }
}

export async function getPartnerConfig(clientId: string) {
  const data =
    await portalApiClient.get_order_creation_client_configuration_v2_clients_orders_partner_config_get(
      {
        queries: { partner_id: clientId },
      },
    )
  return { data }
}

async function pollForOrder(
  clientId: string,
  orderId: string,
  maxAttempts: number = 600, // 5 minutes max (600 * 500ms = 300 seconds)
  delayMs: number = 500,
): Promise<boolean> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      // Check if order appears in recent orders
      const response = await getRecentOrders(clientId, 1, 100)
      if (response.error) {
        // If error, wait and retry
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, delayMs))
          continue
        }
        return false
      }

      if (!response.data) {
        // No data yet, wait and retry
        if (attempt < maxAttempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, delayMs))
          continue
        }
        return false
      }

      const orderIds = getOrderIdsFromRecentResponse(response.data)
      if (orderIds.has(orderId)) {
        return true
      }

      // Order not found yet, wait and retry
      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs))
        continue
      }
      return false
    } catch {
      // For errors, wait and retry
      if (attempt < maxAttempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs))
        continue
      }
      return false
    }
  }
  return false
}

function getPortalApiErrorMessage(e: unknown, fallback: string): string {
  let errorMessage = e instanceof Error ? e.message : fallback

  const response = e as {
    response?: {
      data?: { detail?: unknown }
      status?: number
    }
  }
  const detail = response.response?.data?.detail
  if (typeof detail === 'string') {
    errorMessage = detail
  } else if (
    Array.isArray(detail) &&
    detail[0] &&
    typeof detail[0] === 'object' &&
    'msg' in detail[0]
  ) {
    errorMessage = (detail[0] as { msg?: string }).msg ?? errorMessage
  }

  return errorMessage
}

export async function addKitEvent(
  clientId: string,
  kitId: string,
  payload: AddKitEventDTOType,
  axiosConfig: Pick<AxiosRequestConfig, 'signal'> = {},
): Promise<void> {
  try {
    await portalApiClient.add_kit_event_v2_clients_orders__kit_id__events_post(payload, {
      params: { kit_id: kitId },
      queries: { partner_id: clientId },
      ...axiosConfig,
    })
  } catch (e) {
    throw new Error(getPortalApiErrorMessage(e, 'Error adding kit event: ' + String(e)))
  }
}

export async function setTestOrderStatus(
  clientId: string,
  kitId: string,
  orderId: string,
  payload: SetTestOrderStatusDTOType,
) {
  // Poll for the order to be available before setting status
  const orderExists = await pollForOrder(clientId, orderId)
  if (!orderExists) {
    return {
      error: 'Order not found after placement. Please try setting the status manually.',
      data: null,
    }
  }

  // Extra delay so downstream systems can process the order before we set status.
  await new Promise((resolve) => setTimeout(resolve, 30_000))

  try {
    const data =
      await portalApiClient.set_test_order_status_v2_clients_orders__kit_id__statuses_post(
        payload,
        {
          params: { kit_id: kitId },
          queries: { partner_id: clientId },
        },
      )
    return { error: null, data }
  } catch (e) {
    let errorMessage = e instanceof Error ? e.message : 'Error setting test order status: ' + e

    if (isErrorFromPath(portalApiClient.api, 'post', '/v2/clients/orders/:kit_id/statuses', e)) {
      const response = e.response as { data?: { detail?: unknown }; status?: number } | undefined
      const detail = response?.data?.detail
      if (typeof detail === 'string') {
        errorMessage = detail
      } else if (
        Array.isArray(detail) &&
        detail[0] &&
        typeof detail[0] === 'object' &&
        'msg' in detail[0]
      ) {
        errorMessage = (detail[0] as { msg?: string }).msg ?? errorMessage
      } else if (e instanceof Error && e.message) {
        errorMessage = e.message
      }
      // Include status code in error message for 500 errors
      if (response?.status === 500) {
        errorMessage = `Internal server error (500): ${errorMessage}`
      }
    }

    return {
      error: errorMessage,
      data: null,
    }
  }
}

export async function requestTestOrderCorrection(
  clientId: string,
  kitId: string,
  payload: RequestMultipleTestOrderCorrectionsDTOType,
) {
  try {
    const data =
      await portalApiClient.request_test_order_corrections_v3_clients_orders__kit_id__corrections_post(
        payload,
        {
          params: { kit_id: kitId },
          queries: { partner_id: clientId },
        },
      )
    return { error: null, data: data as RequestMultipleTestOrderCorrectionsSchemaType }
  } catch (e) {
    let errorMessage =
      e instanceof Error ? e.message : 'Error requesting test order correction: ' + e

    const response = e as {
      response?: {
        data?: { detail?: unknown }
        status?: number
      }
    }
    const detail = response.response?.data?.detail
    if (typeof detail === 'string') {
      errorMessage = detail
    } else if (
      Array.isArray(detail) &&
      detail[0] &&
      typeof detail[0] === 'object' &&
      'msg' in detail[0]
    ) {
      errorMessage = (detail[0] as { msg?: string }).msg ?? errorMessage
    }

    return {
      error: errorMessage,
      data: null,
    }
  }
}

/**
 * Fetches result PDF URLs for a given kit.
 */
export const getOrderResultsPdfs = async (
  partnerId: string,
  kitId: string,
  options?: { fullHistory?: boolean },
) => {
  try {
    const data = await portalApiClient.results_by_kit_id_v2_clients_orders_results__kit_id__get({
      params: { kit_id: kitId },
      queries: {
        partner_id: partnerId,
        full_history: options?.fullHistory ?? true,
      },
    })
    return { error: null, data }
  } catch (error) {
    const normalizedError =
      error instanceof Error ? error : new Error('Failed to fetch order results', { cause: error })
    return { error: normalizedError, data: null }
  }
}

export const getOrderDetails = async (
  partnerId: string,
  kitId: string,
  axiosConfig: Pick<AxiosRequestConfig, 'signal'> = {},
) => {
  const result =
    await portalApiClient.get_order_details_with_shipping_and_results_v2_clients_orders__kit_id__details_get(
      {
        params: { kit_id: kitId },
        queries: { partner_id: partnerId },
        ...axiosConfig,
      },
    )
  return result
}

export const getOrderPcpInfo = async (
  partnerId: string,
  kitId: string,
  axiosConfig: Pick<AxiosRequestConfig, 'signal'> = {},
) => {
  const result =
    await portalApiClient.get_client_order_pcp_info_v2_clients_orders__kit_id__pcp_info_get({
      params: { kit_id: kitId },
      queries: { partner_id: partnerId },
      ...axiosConfig,
    })
  return result
}

export const getOrderEligibilityQuestionnaire = async (
  partnerId: string,
  kitId: string,
  axiosConfig: Pick<AxiosRequestConfig, 'signal'> = {},
) => {
  try {
    const result =
      await portalApiClient.get_eligibility_questionnaire_responses_v2_clients_orders__kit_id__eligibility_questionnaire_get(
        {
          params: { kit_id: kitId },
          queries: { partner_id: partnerId },
          ...axiosConfig,
        },
      )
    return result
  } catch (error: unknown) {
    if (isHttpNotFoundError(error)) {
      return { responses: [] }
    }
    throw error
  }
}

export const validateClientKit = async (
  partnerId: string,
  kitId: string,
  axiosConfig: Pick<AxiosRequestConfig, 'signal'> = {},
) => {
  return portalApiClient.validate_client_kit_v2_clients_orders__kit_id__validate_post(undefined, {
    params: { kit_id: kitId },
    queries: { partner_id: partnerId },
    ...axiosConfig,
  })
}

export const registerClientKit = async (
  partnerId: string,
  kitId: string,
  payload: ClientRegisterKitDTOType,
  axiosConfig: Pick<AxiosRequestConfig, 'signal'> = {},
) => {
  return portalApiClient.register_client_kit_v2_clients_orders__kit_id__registration_post(payload, {
    params: { kit_id: kitId },
    queries: { partner_id: partnerId },
    ...axiosConfig,
  })
}
